import { useMemo } from "react";

import { GroupedInclusionPatient } from "@/models/GroupedInclusionModel.ts";
import { InclusionAgentPatient } from "@/models/InclusionAgentModel.ts";
import { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";

export const useCanViewInclusionCenter = () => {
  const { data: featureFlags } = Queries.user.useFeatureFlags({});

  return !!featureFlags?.["inclusion-agent"];
};

type InclusionCenterContent =
  | {
      type: "agent";
      patient: InclusionAgentPatient;
    }
  | {
      type: "nurse";
      patient: NurseInclusionPatient;
    }
  | {
      type: "group";
      patient: GroupedInclusionPatient;
    };

export const useInclusionCenterContent = (): InclusionCenterContent[] => {
  const inclusionAgent = Queries.practitioner.useInclusionAgentPatients({});
  const grouped = Queries.practitioner.useGroupedInclusionPatients({});
  const nurseInclusion = Queries.practitioner.useNurseInclusionPatients({});

  return useMemo(() => {
    const list = [
      ...nurseInclusion.map(patient => ({
        type: "nurse" as const,
        patient,
      })),
      ...grouped.map(patient => ({
        type: "group" as const,
        patient,
      })),
      ...inclusionAgent.map(patient => ({
        type: "agent" as const,
        patient,
      })),
    ];

    return list.toSorted((a, b) => {
      const statusA = getPatientStatus(a);
      const statusB = getPatientStatus(b);
      if (statusA === "accepted") {
        return -1;
      } else if (statusB === "accepted") {
        return 1;
      }
      return 0;
    });
  }, [inclusionAgent, grouped, nurseInclusion]);
};

const getPatientStatus = (item: InclusionCenterContent): "accepted" | "suggestion" => {
  switch (true) {
    case item.type === "agent" && item.patient.status === "patient_accepted":
      return "accepted";
    case item.type === "agent" && item.patient.status === "suggested_to_doc":
      return "suggestion";
    case item.type === "nurse":
      return "suggestion";
    case item.type === "group":
      return "accepted";
  }
  throw new Error("Invalid item type");
};
